import { useQuery, useMutation } from "@apollo/client";
import { AnonymousTeamFilterDropdown } from "@components/teams/AnonymousTeamFilterDropdown";
import { useAuth, useTeam, usePremiumFeature } from "@hooks";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import AutoTemplatesList from "./AutoTemplatesList";
import TemplatesFreePlanState from "./empty-states/TemplatesFreePlanState";
import { createNewTemplate } from "./utils";
import KnowledgeIsPawer from "../../assets/imgs/knowledge-is-pawer.svg";
import EmptyStateImg from "../../assets/imgs/template-empty-state.svg";
import {
  UPDATE_AUTO_TEMPLATE,
  CREATE_AUTO_TEMPLATE,
} from "../../graphql/mutations/AutoTemplates";
import { GET_AUTO_TEMPLATES } from "../../graphql/queries/AutoTemplates";
import { alert } from "../common/Alert";
import { BasicButton } from "../common/buttons/BasicButton";
import EmphasisBanner from "../common/EmphasisBanner";
import EmptyState from "../common/loading/EmptyState";

const AutoTemplatesPage = () => {
  const navigate = useNavigate();
  const { hasFeatureAccess, UserPermissions } = usePremiumFeature();

  const { currentTeam } = useTeam();
  const { userUuid } = useAuth();
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([
    userUuid,
  ]);
  const { data, refetch, loading } = useQuery(GET_AUTO_TEMPLATES, {
    variables: {
      teamUuid: currentTeam?.uuid,
    },
  });
  const [updateAutoTemplate] = useMutation(UPDATE_AUTO_TEMPLATE);
  const [createAutoTemplate] = useMutation(CREATE_AUTO_TEMPLATE);
  const templates = data?.autoTemplates || [];
  const filteredTemplates =
    templates.filter(
      (i) =>
        !i?.user?.uuid || selectedTeamMembers.includes(i.user.uuid),
    ) || [];
  const { freeMode } = useFlags();

  const handleTemplateClick = (template) => {
    navigate(`/templates/edit/${template.uuid}`);
  };

  const handleFavouriteToggle = async (template) => {
    await updateAutoTemplate({
      variables: {
        input: {
          templateUuid: template?.uuid,
          isFavourite: !template?.isFavourite,
        },
      },
    });
    let message = "";
    if (template.isFavourite) {
      message =
        "You've removed this template from your favorites. It will no longer appear at the top of your templates list.";
    } else {
      message =
        "You've favorited this template. It will now appear at the top of your templates list.";
    }
    alert("success", message);
    refetch();
  };

  const handleCreateNewTemplate = async () => {
    const templateUuid = await createNewTemplate(
      "SOAP",
      true,
      createAutoTemplate,
    );
    navigate(`/templates/edit/${templateUuid}?isNew=true`);
  };

  useEffect(refetch, [refetch]);

  if (loading) {
    return null;
  }

  if (
    freeMode &&
    !hasFeatureAccess(UserPermissions.ADAPTIVE_TEMPLATES_ENABLED)
  ) {
    return <TemplatesFreePlanState />;
  }

  return (
    <div className="relative min-h-[80vh] flex flex-col">
      <div
        className={"flex justify-between gap-4 flex-col sm:flex-row"}
      >
        <div className={"flex flex-col justify-center"}>
          <h1>Adaptive Templates</h1>
          <h4 className="text-sm text-gray-500 dark:text-gray-400 my-2 font-medium">
            Scribenote can <i>Scribe Like You</i>. Adaptive Templates
            allow Scribenote to adapt to your existing templates,
            preferences, and writing style.
          </h4>
        </div>

        <BasicButton
          onClick={handleCreateNewTemplate}
          type="button"
          importance="primary"
          styles={"whitespace-nowrap"}
          dataCy={"confirmButton"}
          dataTestId="new-adaptive-template-button"
        >
          + New Adaptive Template
        </BasicButton>
      </div>
      {templates.length === 0 && !loading && (
        <EmphasisBanner>
          <div className="flex flex-row items-center justify-between w-full px-4">
            <div className="flex flex-col items-start space-y-4">
              <h4 className="max-w-[600px] text-gray-700">
                Ready to make Scribenote your personalized AI Scribe?
                Read our docs to learn how to set up your first
                adaptive template.
              </h4>
              <a
                href="https://docs.scribenote.com/en/articles/10496596" // CHECK THAT THE ID IS CORRECT
                target="_blank"
                rel="noreferrer"
                className="self-start bg-indigo-500 text-white px-4 py-2 rounded-full hover:bg-indigo-600 hover:text-white transition-all"
              >
                Read Docs →
              </a>
            </div>

            <img src={KnowledgeIsPawer} className="w-48" />
          </div>
        </EmphasisBanner>
      )}

      {/* Return null if loading because the query is really fast and having a normal loader would look jumpy */}
      {loading ? null : templates.length > 0 ? (
        <>
          {currentTeam?.uuid && (
            <div className="flex mt-1">
              <AnonymousTeamFilterDropdown
                selectedTeamMembers={selectedTeamMembers}
                setSelectedTeamMembers={setSelectedTeamMembers}
              />
            </div>
          )}
          <div className="mt-4 p-8 space-y-10 rounded-xl bg-gradient-to-b from-indigo-50 dark:from-indigo-900 via-transparent dark:via-transparent to-transparent dark:to-transparent dark:bg-gray-800">
            <AutoTemplatesList
              title="Favorites"
              subtitle="These templates will appear at the top of your recording dropdown."
              templates={filteredTemplates}
              templateType="FAVOURITES"
              onTemplateClick={handleTemplateClick}
              onFavouriteToggle={handleFavouriteToggle}
              dataTestId="favorite-templates-list"
            />

            <AutoTemplatesList
              title="SOAP Format"
              templates={filteredTemplates}
              templateType="SOAP"
              onTemplateClick={handleTemplateClick}
              onFavouriteToggle={handleFavouriteToggle}
            />

            <AutoTemplatesList
              title="Freeform"
              templates={filteredTemplates}
              templateType="CUSTOM"
              onTemplateClick={handleTemplateClick}
              onFavouriteToggle={handleFavouriteToggle}
            />
          </div>
        </>
      ) : (
        <div className="flex-grow flex items-center justify-center">
          <EmptyState
            title="No templates here yet."
            img={EmptyStateImg}
          />
        </div>
      )}
    </div>
  );
};

export default AutoTemplatesPage;
